import axios from 'axios';
import { LOGIN_PATH, USER_AUTH_PATH } from './constants/auth';
import { HTTP_STATUS } from './constants/httpStatus';

class Api {
	constructor() {
		this.axios = this.createAxios();
		this.refreshPromise = null;
		this.isEndingSession = false;
		this.useInterceptors();
	}

	createAxios() {
		return axios.create({
			baseURL: process.env.REACT_APP_CRM_URL,
			withCredentials: true,
		});
	}

	refreshAccessToken() {
		if (!this.refreshPromise) {
			this.refreshPromise = this.axios
				.post(USER_AUTH_PATH.REFRESH_TOKEN)
				.finally(() => {
					this.refreshPromise = null;
				});
		}

		return this.refreshPromise;
	}

	async endSession() {
		if (this.isEndingSession) {
			return;
		}

		this.isEndingSession = true;

		try {
			await this.axios.post(USER_AUTH_PATH.LOGOUT);
		} catch (error) {
			void error;
		}

		const isAlreadyOnLogin = window.location.pathname === LOGIN_PATH;
		if (isAlreadyOnLogin) {
			this.isEndingSession = false;
			return;
		}

		window.location = LOGIN_PATH;
	}

	useInterceptors() {
		this.axios.interceptors.response.use(
			(response) => response,
			async (error) => {
				const status = error.response?.status;
				const originalRequest = error.config;
				const requestUrl = originalRequest?.url || '';
				const isAuthRequest =
					requestUrl.includes(USER_AUTH_PATH.REFRESH_TOKEN) ||
					requestUrl.includes(USER_AUTH_PATH.LOGIN) ||
					requestUrl.includes(USER_AUTH_PATH.LOGOUT)

				if (status !== HTTP_STATUS.UNAUTHORIZED) {
					if (status === HTTP_STATUS.FORBIDDEN) {
						await this.endSession();
						throw error;
					}
					if (error.response?.data?.message) {
						throw new Error(error.response.data.message);
					}

					throw error;
				}

				const shouldSkipRefresh =
					!originalRequest || originalRequest._retry || isAuthRequest;
				if (shouldSkipRefresh) {
					await this.endSession();
					throw error;
				}

				originalRequest._retry = true;

				try {
					await this.refreshAccessToken();
					return this.axios.request(originalRequest);
				} catch (refreshError) {
					await this.endSession();
					throw refreshError;
				}
			},
		);
	}
}
const api = new Api();

export default api;
